import { useForm } from "@tanstack/react-form";
import {
  InputContainer,
  Button,
  InputSelect,
  InputText,
} from "@sellar-io/components";
import { TwoBusinessRegistrationCreateRequestType } from "@sellar-io/api-v2/types";
import dayjs from "dayjs";

export type ProprietorDetailsFormType =
  TwoBusinessRegistrationCreateRequestType["proposedBusiness"]["proprietorDetails"];

const TITLE_OPTIONS = [
  { value: "", label: "Select a title" },
  { value: "Mr", label: "Mr" },
  { value: "Mrs", label: "Mrs" },
  { value: "Ms", label: "Ms" },
  { value: "Miss", label: "Miss" },
  { value: "Mx", label: "Mx" },
  { value: "Dr", label: "Dr" },
];

const fieldLabels: Record<string, string> = {
  title: "Title",
  firstName: "First Name",
  middleNames: "Middle Names (optional)",
  lastName: "Last Name",
  email: "Email",
  phone: "Phone",
  dateOfBirth: "Date of Birth",
  apartment: "Apartment (optional)",
  building: "Building",
  street: "Street",
  postalCode: "Postal Code",
  city: "City",
  region: "Region",
  countryCode: "Country Code",
  dateFrom: "From",
  dateUntil: "Until",
  identityNumber: "National Insurance Number",
};

const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
const phoneRegex = /^\+?\d{7,15}$/;
const niRegex = /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]?$/i; // UK NI number pattern
const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const ProprietorDetailsForm = ({
  defaultValues,
  onSave,
  loading = false,
}: {
  defaultValues?: ProprietorDetailsFormType;
  onSave: (data: ProprietorDetailsFormType) => void;
  loading?: boolean;
}) => {
  const form = useForm({
    defaultValues: defaultValues ?? {
      title: "",
      firstName: "",
      middleNames: null,
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      addressHistory: [
        {
          apartment: null,
          building: "",
          street: "",
          postalCode: "",
          city: "",
          region: "",
          countryCode: "GB" as const,
          dateFrom: "",
          dateUntil: null,
        },
      ],
      nationalIdentityNumbers: [
        {
          identityNumberType: "SOCIAL_SECURITY_NUMBER" as const,
          identityNumber: "",
        },
      ],
    },
    onSubmit: ({ value }) => {
      onSave(value);
    },
    onSubmitInvalid: (result) => {
      const errorFields = Object.keys(result.formApi.getAllErrors().fields);
      const firstInvalidField = errorFields[0];

      if (firstInvalidField) {
        const el = document.getElementsByName(firstInvalidField)[0];

        el?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        el?.focus();
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex flex-col gap-8"
    >
      {/* Personal Details */}
      <section className="space-y-4">
        <h2 className="border-b pb-2 text-xl font-semibold">
          1. Personal Details
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {(
            [
              "title",
              "firstName",
              "middleNames",
              "lastName",
              "email",
              "phone",
            ] as const
          ).map((field) => (
            <form.Field
              key={field}
              name={field}
              validators={{
                onChange: ({ value }) => {
                  if (field === "title" && !value) return "Title is required";
                  if (field === "firstName" && (!value || !value.trim()))
                    return "First name is required";
                  if (field === "lastName" && (!value || !value.trim()))
                    return "Last name is required";
                  if (field === "email" && (!value || !emailRegex.test(value)))
                    return "Enter a valid email";
                  if (field === "phone" && (!value || !phoneRegex.test(value)))
                    return "Enter a valid phone number";
                  return undefined;
                },
              }}
            >
              {(fieldState) => (
                <div className="flex flex-col gap-1">
                  <label className="font-medium">
                    {fieldLabels[field]}
                    {[
                      "title",
                      "firstName",
                      "lastName",
                      "email",
                      "phone",
                    ].includes(field) && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </label>
                  <InputContainer>
                    {field === "title" ? (
                      <InputSelect
                        value={fieldState.state.value ?? ""}
                        onChange={(e) =>
                          fieldState.handleChange(e.target.value)
                        }
                        className="w-full"
                      >
                        {TITLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </InputSelect>
                    ) : (
                      <InputText
                        name={field}
                        defaultValue={fieldState.state.value ?? ""}
                        onChange={(e) =>
                          fieldState.handleChange(e.target.value)
                        }
                        placeholder={`Enter ${fieldLabels[field]}`}
                      />
                    )}
                  </InputContainer>
                  {!fieldState.state.meta.isValid && (
                    <span className="text-sm text-red-500">
                      {fieldState.state.meta.errors.join(", ")}
                    </span>
                  )}
                </div>
              )}
            </form.Field>
          ))}

          {/* Date of Birth */}
          <form.Field
            name="dateOfBirth"
            validators={{
              onChange: ({ value }) => {
                const isValidDate = value && dateRegex.test(value);

                if (!isValidDate) {
                  return "Enter a valid date";
                }

                const isAgeOldEnough = dayjs(value).isBefore(
                  dayjs().subtract(18, "years"),
                );

                if (!isAgeOldEnough) {
                  return "Applicant must be 18 or over";
                }

                return undefined;
              },
            }}
          >
            {(fieldState) => (
              <div className="flex flex-col gap-1">
                <label className="font-medium">
                  Date of Birth <span className="ml-1 text-red-500">*</span>
                </label>
                <InputContainer>
                  <InputText
                    name="dateOfBirth"
                    type="date"
                    defaultValue={fieldState.state.value}
                    onChange={(e) => fieldState.handleChange(e.target.value)}
                  />
                </InputContainer>
                {!fieldState.state.meta.isValid && (
                  <span className="text-sm text-red-500">
                    {fieldState.state.meta.errors.join(", ")}
                  </span>
                )}
              </div>
            )}
          </form.Field>
        </div>
      </section>

      {/* National Insurance Numbers */}
      <section className="space-y-4">
        <h2 className="border-b pb-2 text-xl font-semibold">
          2. National Insurance Number
        </h2>
        <form.Field name="nationalIdentityNumbers" mode="array">
          {(field) => (
            <div className="space-y-4">
              {field.state.value.map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 rounded border p-4 shadow-sm"
                >
                  <form.Field
                    name={`nationalIdentityNumbers[${i}].identityNumber`}
                    validators={{
                      onChange: ({ value }) =>
                        !value || !niRegex.test(value)
                          ? "Enter a valid NI number (e.g., QQ123456C)"
                          : undefined,
                    }}
                  >
                    {(niField) => (
                      <div className="flex flex-col gap-1">
                        <label className="font-medium">
                          NI Number <span className="ml-1 text-red-500">*</span>
                        </label>
                        <InputContainer>
                          <InputText
                            name={`nationalIdentityNumbers[${i}].identityNumber`}
                            defaultValue={niField.state.value}
                            onChange={(e) =>
                              niField.handleChange(e.target.value)
                            }
                            placeholder="QQ123456C"
                          />
                        </InputContainer>
                        {!niField.state.meta.isValid && (
                          <span className="text-sm text-red-500">
                            {niField.state.meta.errors.join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                  </form.Field>
                </div>
              ))}
            </div>
          )}
        </form.Field>
      </section>

      {/* Address History */}
      <section className="space-y-4">
        <h2 className="border-b pb-2 text-xl font-semibold">
          3. Address History
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Please provide your address history covering the last{" "}
          <strong>5 years</strong>. The first address must be your current
          address and the 'until date' must not be provide. All other addresses
          must have both from and until dates. Ensure that the 'From' and 'To'
          dates overlap between multiple addresses.
        </p>
        <form.Field name="addressHistory" mode="array">
          {(field) => (
            <div className="space-y-4">
              {field.state.value.map((_, i) => (
                <div key={i} className="space-y-4 rounded border p-4 shadow-sm">
                  <h3 className="text-lg font-medium">Address {i + 1}</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {(
                      [
                        "apartment",
                        "building",
                        "street",
                        "postalCode",
                        "city",
                        "region",
                        "countryCode",
                        "dateFrom",
                        "dateUntil",
                      ] as const
                    ).map((subField) => {
                      // don't show dateUntil field for first address
                      if (subField === "dateUntil" && i === 0) {
                        return null;
                      }

                      return (
                        <form.Field
                          key={subField}
                          name={`addressHistory[${i}].${subField}`}
                          validators={{
                            onChange: ({ value, fieldApi }) => {
                              if (subField === "apartment") return undefined;
                              if (subField === "dateUntil" && i === 0)
                                return undefined;

                              if (
                                ["dateUntil", "dateFrom"].includes(subField)
                              ) {
                                const isValidDate =
                                  value && dateRegex.test(value);

                                if (!isValidDate) {
                                  return "Enter a valid date";
                                }

                                const isDateInFuture = dayjs(value).isAfter(
                                  dayjs().subtract(1, "day"),
                                );

                                if (isDateInFuture) {
                                  return "Date must be before yesterday";
                                }

                                if (i > 0) {
                                  const dateFrom = fieldApi.form.getFieldValue(
                                    `addressHistory[${i}].dateFrom`,
                                  );
                                  const dateUntil = fieldApi.form.getFieldValue(
                                    `addressHistory[${i}].dateUntil`,
                                  );

                                  if (
                                    !!dateFrom &&
                                    !!dateUntil &&
                                    dayjs(dateFrom).isAfter(dayjs(dateUntil))
                                  ) {
                                    return "'From' date must be before 'To' date";
                                  }
                                }
                              }

                              if (!value || !value.toString().trim()) {
                                return `${fieldLabels[subField]} is required`;
                              }

                              return undefined;
                            },
                          }}
                        >
                          {(addrField) => (
                            <div className="flex flex-col gap-1">
                              <label className="font-medium">
                                {fieldLabels[subField]}
                                {!["apartment"].includes(subField) &&
                                  !(subField === "dateUntil" && i === 0) && (
                                    <span className="ml-1 text-red-500">*</span>
                                  )}
                              </label>
                              <InputContainer>
                                <InputText
                                  name={`addressHistory[${i}].${subField}`}
                                  type={
                                    subField.includes("date") ? "date" : "text"
                                  }
                                  defaultValue={addrField.state.value ?? ""}
                                  onChange={(e) =>
                                    addrField.handleChange(e.target.value)
                                  }
                                  disabled={subField === "countryCode"}
                                  placeholder={`Enter ${fieldLabels[subField]}`}
                                />
                              </InputContainer>
                              {!addrField.state.meta.isValid && (
                                <span className="text-sm text-red-500">
                                  {addrField.state.meta.errors.join(", ")}
                                </span>
                              )}
                            </div>
                          )}
                        </form.Field>
                      );
                    })}
                  </div>

                  {field.state.value.length > 1 && (
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => field.removeValue(i)}
                    >
                      Remove Address
                    </Button>
                  )}
                </div>
              ))}

              <Button
                type="button"
                onClick={() =>
                  field.pushValue({
                    apartment: null,
                    building: "",
                    street: "",
                    postalCode: "",
                    city: "",
                    region: "",
                    countryCode: "GB",
                    dateFrom: "",
                    dateUntil:
                      field.state.value[field.state.value.length - 1]
                        ?.dateFrom ?? null,
                  })
                }
              >
                + Add Address
              </Button>
            </div>
          )}
        </form.Field>
      </section>
      {/* Submit Button */}
      <div className="pt-6">
        <Button
          type="submit"
          size="large"
          disabled={loading}
          onClick={() => {
            console.log("called");
            form.handleSubmit();
          }}
        >
          Next: Business Details
        </Button>
      </div>
    </form>
  );
};
